import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import * as icons from "react-bootstrap-icons";
import { BidReg } from "../../redux/auth-reducer/auth-service";
import { readApiMessage } from "../../utils/helper/helper";
import { teamLotBidBody } from "../../utils/helper/lotPage";
import { formatUsdWhole } from "../../utils/helper/money";
import { ACTION_LABELS, AUCTION_ACTIONS } from "../../utils/helper/auctionList";
import {
  BID_AREA,
  amountOptions,
  bidAreaContent,
  bidAreaInfo,
  bidHint,
  bidPlacedText,
  cleanAmountInput,
  confirmBidText,
  isLeader,
  validateBid,
} from "../../utils/helper/auctionDetails";

/** Custom Bid Amount; the page has one bid area. */
const AMOUNT_INPUT_ID = "auction-details-bid-amount";

/**
 * "Unlock this auction to place bids" (#238, State 7). Unlock opens the existing pass dialog, which
 * asks the server what is on offer: a pass is sold until bidding on the auction closes, before the
 * start (AC8) and while it runs (#289), so the sale goes through on the live team too; once bidding
 * has closed the dialog shows the server's refusal. A player with a pass credit is offered "Use
 * credit", as on the Auctions list.
 */
const UnlockPrompt = ({ auctionName, useCredit, onUnlock }) => (
  <div className="auction-details-locked">
    <div className="auction-details-locked-icon" aria-hidden="true">
      <icons.Lock />
    </div>
    <div className="auction-details-locked-text">
      <b>Unlock this auction to place bids</b>
      <p>A one-time payment gives you bidding access to every team in {auctionName}.</p>
    </div>
    <button type="button" className="auction-action unlock auction-details-unlock" onClick={onUnlock}>
      {ACTION_LABELS[useCredit ? AUCTION_ACTIONS.CREDIT : AUCTION_ACTIONS.UNLOCK]}
    </button>
  </div>
);

/**
 * "Place Your Bid" (#238, States 4-6): the amount buttons, Custom Bid Amount and Place Bid. A bid is
 * checked here (validateBid), confirmed in a dialog, then sent with the lot page's body
 * (teamLotBidBody); the server decides and its answer, accepted or refused, is shown under the
 * controls. Whatever it answered, the page reads the auction again, so the figures are the server's.
 *
 * The bidding rules stay the server's: Place Bid is disabled only while a bid is on its way and once
 * the team's clock shows 00:00, when the server takes no more (#191).
 */
const BidControls = ({ lot, bidTime, userId, secondsLeft, initialAmount, onReload }) => {
  // #238 AC14: the outbid pop-up's Place Bid opens the page with the Next Minimum Bid filled in.
  const [amount, setAmount] = useState(initialAmount != null ? String(initialAmount) : "");
  const [error, setError] = useState(null);
  const [placed, setPlaced] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [sending, setSending] = useState(false);

  const options = amountOptions(lot.minNextBid, lot.bidIncrement);
  const timeIsUp = secondsLeft <= 0;

  const pick = (value) => {
    setAmount(String(value));
    setError(null);
  };

  const type = (event) => {
    setAmount(cleanAmountInput(event.target.value));
    setError(null);
  };

  const submit = () => {
    const result = validateBid(amount, {
      minNextBid: lot.minNextBid,
      increment: lot.bidIncrement,
      leads: isLeader(lot, userId),
    });
    if (result.error) {
      setError(result.error);
      setPlaced(null);
      return;
    }
    setConfirming(result.amount);
  };

  const confirm = async () => {
    const value = confirming;
    setSending(true);
    const res = await BidReg(teamLotBidBody({ teamId: lot.teamId, bidTime }, lot, value, userId));
    setSending(false);
    setConfirming(null);

    if (res?.data?.status === true) {
      setPlaced(bidPlacedText(value));
      setError(null);
      setAmount("");
    } else if (res) {
      // No reply at all has already been reported by apiClient; a refusal or a fault says why.
      setError(readApiMessage(res));
      setPlaced(null);
    }
    onReload();
  };

  return (
    <>
      <h3 className="auction-details-bid-title">Place Your Bid</h3>
      <div className="auction-details-chips">
        {options.map((option) => {
          const on = amount !== "" && Number(amount) === option.amount;
          return (
            <button
              key={option.caption}
              type="button"
              className={`auction-details-chip${on ? " on" : ""}`}
              aria-pressed={on}
              onClick={() => pick(option.amount)}
            >
              <b>{formatUsdWhole(option.amount)}</b>
              <span>{option.caption}</span>
            </button>
          );
        })}
      </div>
      <div className="auction-details-bid-row">
        <div className="auction-details-bid-label">
          <label htmlFor={AMOUNT_INPUT_ID}>Custom Bid Amount</label>
          <span className="auction-details-bid-input">
            <span aria-hidden="true">$</span>
            <input
              id={AMOUNT_INPUT_ID}
              type="text"
              inputMode="decimal"
              placeholder={`${lot.minNextBid} or more`}
              value={amount}
              onChange={type}
              className={error ? "error" : undefined}
              aria-invalid={Boolean(error)}
            />
          </span>
        </div>
        <button
          type="button"
          className="auction-action bid auction-details-place"
          onClick={submit}
          disabled={sending || timeIsUp}
        >
          Place Bid
        </button>
      </div>
      {error && (
        <div className="auction-details-error" role="alert">
          <icons.ExclamationCircle aria-hidden="true" />
          {error}
        </div>
      )}
      {placed && (
        <div className="auction-details-placed" role="status">
          <icons.CheckCircleFill aria-hidden="true" />
          {placed}
        </div>
      )}
      <div className="auction-details-hint">{bidHint(lot.minNextBid, lot.bidIncrement)}</div>

      <Modal show={confirming !== null} centered onHide={sending ? undefined : () => setConfirming(null)}>
        <Modal.Header className="promo-code-header promo-close" closeButton={!sending}>
          <Modal.Title>
            <b>Confirm your bid</b>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{confirmBidText(confirming, lot.teamName)}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirming(null)} disabled={sending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirm} disabled={sending}>
            {sending ? "Placing..." : "Confirm"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

/**
 * The bid area under the selected team (#238): exactly one of the Unlock prompt, the bidding
 * controls, or an info line, by bidAreaContent. Keyed on the team by the page, so selecting another
 * team clears the amount and the messages (#238 AC13). Before the start, a player who has not
 * unlocked the auction gets the Unlock prompt with the start line under it (AC8).
 *
 * @param {object} props
 * @param {object} props.lot The selected lot.
 * @param {object} props.details The auction (auctionName, startDate, bidTime, canBid).
 * @param {boolean} props.auctionStarted Whether the auction has started.
 * @param {*} props.userId The player.
 * @param {number} props.secondsLeft The running team's countdown.
 * @param {number} [props.initialAmount] The amount to start the controls with (the outbid pop-up's Place Bid).
 * @param {() => void} props.onUnlock Opens the pass dialog.
 * @param {() => void} props.onReload Reads the auction again.
 */
export const AuctionBidArea = ({ lot, details, auctionStarted, userId, secondsLeft, initialAmount, onUnlock, onReload }) => {
  const content = bidAreaContent(lot, { canBid: Boolean(details.canBid), auctionStarted });
  const useCredit = Boolean(details.canRedeemAuctionPassCredit);

  if (content === BID_AREA.UNLOCK) {
    return <UnlockPrompt auctionName={details.auctionName} useCredit={useCredit} onUnlock={onUnlock} />;
  }
  if (content === BID_AREA.UNLOCK_BEFORE_START) {
    return (
      <>
        <UnlockPrompt auctionName={details.auctionName} useCredit={useCredit} onUnlock={onUnlock} />
        <div className="auction-details-info auction-details-info-after">
          {bidAreaInfo(content, { lot, startDate: details.startDate, userId })}
        </div>
      </>
    );
  }
  if (content === BID_AREA.CONTROLS) {
    return (
      <BidControls
        lot={lot}
        bidTime={details.bidTime}
        userId={userId}
        secondsLeft={secondsLeft}
        initialAmount={initialAmount}
        onReload={onReload}
      />
    );
  }
  return (
    <div className="auction-details-info">
      {bidAreaInfo(content, { lot, startDate: details.startDate, userId })}
    </div>
  );
};
